import { apiClient } from './client';

// Two-state model. Legacy values (pending/approved/processing/sent) may still
// arrive from old rows and are treated as "unpaid" by the UI.
export type ExpenseStatus = 'unpaid' | 'paid' | 'overdue';
export type ExpenseType = 'invoice' | 'transfer' | 'tax' | 'mileage' | 'manual';

export interface ExpenseDTO {
  id: string;
  db_id: number;
  expense_type: ExpenseType;
  vendor: string | null;
  short: string | null;
  invoice: string | null;
  category: string | null;
  amount: number;
  currency: string;
  amount_converted?: number;
  preferred_currency?: string;
  // True when convert() fell back to the latest ECB rate because the
  // expense's date was outside the 90-day cache window.
  fx_fallback?: boolean;
  // ISO date of the rate that fallback actually used. Set only when
  // fx_fallback is true, and it moves forward as new rates land.
  fx_rate_date?: string | null;
  tax: number;
  date: string | null;
  due: string | null;
  status: ExpenseStatus;
  confidence: number;
  payment: string | null;
  recurring: boolean;
  source_document_id: number | null;
  details: Record<string, any>;
  notes: string | null;
  created_at: string | null;
  // 'document' rows are uploaded invoices marked "money out" — read-only here;
  // their lifecycle is managed on the Uploaded Invoices page.
  source?: 'manual' | 'document';
  read_only?: boolean;
}

export interface ExpensesListResponse {
  preferred_currency: string;
  expenses: ExpenseDTO[];
}

export interface ExpenseCreatePayload {
  expense_type?: ExpenseType;
  vendor?: string;
  short?: string;
  invoice?: string;
  category?: string;
  amount: number;
  currency: string;
  tax?: number;
  date?: string;
  due?: string;
  status?: ExpenseStatus;
  confidence?: number;
  payment?: string;
  recurring?: boolean;
  source_document_id?: number;
  details?: Record<string, any>;
  notes?: string;
}

export type ExpenseUpdatePayload = Partial<ExpenseCreatePayload>;

// A draft parsed out of free text by the assistant. Nothing is saved until the
// user confirms it, so every field may be null — including `date`, which stays
// unset when the message named a month but no day.
export interface ExpenseDraftFields {
  vendor: string | null;
  amount: number | null;
  currency: string;
  category: string | null;
  date: string | null;
  due: string | null;
  status: ExpenseStatus;
  notes: string | null;
}

export interface ExpenseDraft {
  draft: ExpenseDraftFields;
  /** Fields the user still has to supply before this can be saved. */
  missing: string[];
  /** Human-readable notes about what was inferred, shown under the card. */
  assumptions: string[];
  /** 'YYYY-MM' when a month was named without a day. */
  month_hint?: string | null;
  /** Date-picker bounds for month_hint, as 'YYYY-MM-DD'. */
  date_min?: string;
  date_max?: string;
}

interface EligibilityResponse {
  plan_type: string;
  is_trial: boolean;
  eligible: boolean;
}

export const expensesApi = {
  async eligibility(): Promise<EligibilityResponse> {
    const { data } = await apiClient.get('/api/expenses/eligibility');
    return data;
  },

  async list(params?: { status?: string; category?: string; search?: string }): Promise<ExpensesListResponse> {
    const { data } = await apiClient.get('/api/expenses/', { params });
    return data;
  },

  /** Parse a sentence into an unsaved draft. Writes nothing. */
  async parse(message: string): Promise<ExpenseDraft> {
    const { data } = await apiClient.post('/api/expenses/parse', { message });
    return data;
  },

  async create(payload: ExpenseCreatePayload): Promise<ExpenseDTO> {
    const { data } = await apiClient.post('/api/expenses/', payload);
    return data;
  },

  async update(id: number, payload: ExpenseUpdatePayload): Promise<ExpenseDTO> {
    const { data } = await apiClient.put(`/api/expenses/${id}`, payload);
    return data;
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`/api/expenses/${id}`);
  },

  async getPreferredCurrency(): Promise<string> {
    const { data } = await apiClient.get('/api/expenses/preferred-currency');
    return data.preferred_currency;
  },

  async setPreferredCurrency(code: string): Promise<string> {
    const { data } = await apiClient.put('/api/expenses/preferred-currency', { preferred_currency: code });
    return data.preferred_currency;
  },
};
